import React from 'react';
import { Button, Grid } from '@mui/material';
import { Category as CategoryComponent } from '@Components';
import { setCoFConfig } from '@Actions';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '@Translations/useGlobalization';
import { SetupCategoriesSkeleton } from '@Components/RiskSetup/templates/SetupCategoriesSkeleton.component';
import { NoCoFCategories } from './NoCoFCategories';
import type { Category } from '@Types/category.types';
import type { ComponentProps as OnAddEditProps } from '@Types/component.types';

interface ClassicCoFCategoriesProps extends OnAddEditProps {
	showSkeleton: boolean;
	categories: Category[];
	totalWeight: number;
	handleAddCategoryClicked: () => void;
}

// Pulled out to a top-level function (rather than nested inside the
// onCategoryWeightingChanged callback) to keep function nesting within
// SonarQube's 4-level limit.
const withUpdatedWeighting = (
	categories: Category[],
	index: number,
	value: number,
): Category[] =>
	categories.map((cat, i) =>
		i === index ? { ...cat, weighting: value } : cat,
	);

// The classic (flag-off) CoF Setup layout — kept as its own component, and
// otherwise unchanged from before the guided-configuration redesign, so
// it's easy to diff against main and verify nothing changed for tenants
// without the flag (same approach as Category.component.tsx's Classic*
// split).
export const ClassicCoFCategories: React.FC<ClassicCoFCategoriesProps> = ({
	showSkeleton,
	categories,
	totalWeight,
	onAddComponent,
	onEditComponent,
	handleAddCategoryClicked,
}) => {
	const dispatch = useDispatch();
	const { t } = useGlobalization();

	const renderCategories = () => {
		if (showSkeleton) return <SetupCategoriesSkeleton />;
		if (categories.length === 0) {
			return (
				<NoCoFCategories
					handleAddCategoryClicked={handleAddCategoryClicked}
				/>
			);
		}
		return categories.map((cat, index) => (
			<Grid
				item
				xs={12}
				key={`${cat.name}-${index}`}
				style={{ paddingBottom: '12px' }}>
				<CategoryComponent
					isLoading={false}
					forLoF={false}
					LoFCategoryIndex={index}
					isWeightingInvalid={totalWeight != 100}
					onCategoryWeightingChanged={(value: number) =>
						dispatch(
							setCoFConfig(
								withUpdatedWeighting(categories, index, value),
							),
						)
					}
					onAddComponent={(_forLoF, targetCat) => {
						onAddComponent(false, false, targetCat);
					}}
					onEditComponent={(_forLoF, targetCat, component) => {
						onEditComponent(false, false, targetCat, component);
					}}
				/>
			</Grid>
		));
	};

	return (
		<Grid container>
			<Grid xs={12} item container>
				{!showSkeleton && (
					<Grid item xs={12} container justifyContent="flex-end">
						<Button
							style={{ margin: '12px 0px' }}
							variant="contained"
							color="primary"
							onClick={handleAddCategoryClicked}>
							{t('Add Category')}
						</Button>
					</Grid>
				)}
				{renderCategories()}
			</Grid>
		</Grid>
	);
};
